import { useState, useEffect, useRef } from "react";
import { useParams } from "@remix-run/react";
import RecentSearches from "../common/RecentSearches";
import useEventListener, { useRecentSearches } from "~/lib/hooks/SearchHooks/useRecentSearches";
import { updateRecentSearches } from "~/lib/helpers/autoCompleteSearchHelper";
import { MapPin, CircleX } from "lucide-react";
import { cn } from "~/lib/utils";
import { fetchLocation } from "~/lib/helpers/googleLocationDataManager";
import { getCountryName } from "~/lib/helpers/constants";
import {
  buildSearchUrlFromIpLocation,
  normalizeRegionCode,
  type IpLocationForSearchPath,
} from "~/lib/helpers/ipLocationSearchPathHelper";
import {
  clearSearchPlaceId,
  isValidPersistedPlaceId,
  persistSearchLocationContext,
  persistSearchPlaceId,
  resolvePersistedSearchLocationText,
  stripPlaceIdFromSearchUrl,
} from "~/lib/helpers/searchPlaceContext";
import { convertSearchUrlToLocationPage, isOfficeTypeSlug, parseCountryLandingSegment } from "~/components/blocks/Geosearch/locationGeosearch";
import TelemetryService from "~/lib/services/TelemetryService";
import { shouldShowAutoPlacesOfficeNames } from "~/lib/helpers/autoPlacesOfficeNamesHelper";
import { useDisplayedLocation } from "~/lib/hooks/useDisplayedLocation";

// Internal and Google Places suggestions both come through the backend proxy.
const PLACES_AUTOCOMPLETE_URL = "/api/proxy/places/autocomplete";
const PLACES_RESOLVE_URL = "/api/proxy/places/resolve";
// Don't search until the user has typed at least this many characters.
const MIN_QUERY_LENGTH = 2;
// Wait for a typing pause before hitting the internal and Google APIs.
const AUTOCOMPLETE_DEBOUNCE_MS = 500;
// Session-scoped suggestion cache entries (FIFO-evicted).
const SUGGESTION_CACHE_MAX = 50;

type LocationSuggestion = {
  placeId: string;
  description: string;
  /** Short place name (curated DB rows only) — preferred as the dropdown label. */
  name?: string;
  lat?: number;
  lng?: number;
  city?: string;
  district?: string;
  suburb?: string;
  state?: string;
  stateCode?: string;
  countryCode?: string;
  streetName?: string;
  /** Google's classification of the pick (city/suburb/street/island/POI …).
   * Only genuine sub-city areas (suburb/street/district) earn a deepest URL
   * segment; a POI/island (whose label lead is its own name) does not.
   * Absent for curated DB suggestions. */
  resultType?: string;
  /** Internal DB/property result or Google Places result. */
  source?: "db" | "google";
  /** Dropdown section: locations vs workspace providers vs properties. */
  type?: "place" | "provider" | "property";
  /** Page path for provider/property picks (e.g. "/workspaces/…", "/properties/…"). */
  slug?: string;
};
const deriveLocationFromPath = (pathname: string): string => {
  if (!pathname || pathname === "/") return "";
  const segments = pathname.split("?")[0].split("/").filter(Boolean);
  if (segments.length === 0) return "";

  const countryCode = segments[0];
  const countryName = getCountryName(countryCode);

  const ENTITY_PREFIXES = [
    "properties", "property", "workspaces", "buildings", "areas", "listings", "blog", "news", "api"
  ];
  if (segments.length > 1 && ENTITY_PREFIXES.includes(segments[1])) return "";

  // If 'search' is in the path (e.g. /au/search or /au/search/nsw), remove it so only the actual location is shown.
  const isSearch = segments[1]?.toLowerCase() === "search";
  if (isSearch) {
    segments.splice(1, 1);
  }

  // Strip trailing office-type slug (e.g. "serviced-office") so the search bar
  // shows only the location, not the filter.
  while (segments.length > 1 && isOfficeTypeSlug(segments[segments.length - 1])) {
    segments.pop();
  }

  // For /au/search, removing 'search' leaves only the country, so return country name ('Australia').
  // For standard country root /au, return empty to show search placeholder.
  if (segments.length <= 1) {
    return isSearch ? (countryName || "") : "";
  }

  const capitalize = (str: string) =>
    str
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const lastSeg = segments[segments.length - 1];
  // Country landing (`/ir/office-space-iran`, including a single office type).
  // Show the country, the same way a city or state path shows that place.
  if (
    lastSeg.startsWith("office-space-") ||
    (segments.length === 2 &&
      parseCountryLandingSegment(countryCode, lastSeg)?.officeTypeSlug)
  ) {
    return countryName || "";
  }
  // `/au/enterprise-office-australia` is the same country page with one office type.
  if (
    segments.length === 2 &&
    parseCountryLandingSegment(countryCode, lastSeg)?.officeTypeSlug
  ) {
    if (isSearch) return countryName || "";
    return "";
  }

  const locationName =
    lastSeg.length <= 3 && segments.length === 2
      ? lastSeg.toUpperCase()
      : capitalize(lastSeg);
  const stateName = segments.length > 2 ? segments[1].toUpperCase() : "";

  const parts = [locationName];
  if (stateName && stateName.length <= 3) parts.push(stateName);
  if (countryName) parts.push(countryName);

  return parts.join(", ");
};

const AutoPlaces = ({ onLocationSelect, inputStyleClassName, fixed, mobile, placeholder, focus = false, hideLocateFixed = false, inModal = false, value: externalValue, onSuggestionsVisibilityChange, className }: any) => {
  const routeParams = useParams();
  const location = useDisplayedLocation();
  const showOfficeNames = shouldShowAutoPlacesOfficeNames(location.pathname);

  // Derive initial value from external prop, persisted search context, or route path
  const getInitialAddress = () => {
    if (externalValue !== undefined && externalValue === "") return "";
    if (externalValue) return externalValue;
    const persisted = resolvePersistedSearchLocationText("", null);
    if (persisted) return persisted;
    return deriveLocationFromPath(location.pathname);
  };

  const initialVal = getInitialAddress();
  const [address, setAddress] = useState(initialVal);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const { recentSearches, setRecentSearches } = useRecentSearches();
  const [isSelected, setIsSelected] = useState(!!initialVal);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputWidth, setInputWidth] = useState(0);
  const [isCleared, setIsCleared] = useState(false);
  const isManualClearRef = useRef(false);
  const requestIdRef = useRef(0);

  const suggestionCacheRef = useRef<
    Map<string, { text: string; country: string; data: LocationSuggestion[] }>
  >(new Map());
  /** Highest request id whose results have been painted. A request only
   * paints if newer than the last painter — never a stale overwrite. */
  const lastPaintedIdRef = useRef(0);
  /** Newest in-flight autocomplete request (id + abort handle). */
  const pendingRef = useRef<{
    id: number;
    controller: AbortController;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const withoutOfficeNames = (items: LocationSuggestion[]) =>
    showOfficeNames
      ? items
      : items.filter((item) => (item.type ?? "place") !== "property");

  useEffect(() => {
    if (containerRef.current) {
      setInputWidth(containerRef.current.offsetWidth);
    }
  }, [address, isInputFocused]);

  // Keep search bar text and selected state in sync across navigations and prop updates
  useEffect(() => {
    if (isManualClearRef.current) return;

    // When an explicit empty value is passed (e.g. header search bar),
    // keep it empty — don't derive from URL.
    if (externalValue !== undefined && externalValue === "") {
      setAddress((prev: string) => (prev !== "" ? "" : prev));
      setIsSelected(false);
      return;
    }

    let activeText = externalValue;
    if (!activeText) {
      // After a free-text search the URL carries ?q=<keyword> — show it.
      const urlParams = new URLSearchParams(location.search);
      const queryQ = urlParams.get("q");
      if (queryQ) {
        activeText = queryQ;
      } else {
        activeText = resolvePersistedSearchLocationText("", null) || deriveLocationFromPath(location.pathname);
      }
    }

    if (activeText) {
      setAddress((prev: string) => (prev !== activeText ? activeText : prev));
      setIsSelected(true);
    } else if (externalValue !== undefined) {
      const newValue = externalValue || "";
      setAddress((prev: string) => (prev !== newValue ? newValue : prev));
      setIsSelected(!!newValue);
    }
  }, [externalValue, location.pathname, location.search]);

  // Notify parent about suggestions visibility
  useEffect(() => {
    if (onSuggestionsVisibilityChange) {
      const shouldShowSuggestions = (!isSelected && isInputFocused);
      onSuggestionsVisibilityChange(shouldShowSuggestions);
    }
  }, [isSelected, isInputFocused, onSuggestionsVisibilityChange]);

  const handleChange = (newAddress: any) => {
    setAddress(newAddress);
    setIsSelected(false);
    setIsInputFocused(true);
  };

  const handleClickOutside = (event: Event) => {
    if (!containerRef.current) return;

    const targetNode = event.target as Node | null;
    const isInsideByTarget = !!(targetNode && containerRef.current.contains(targetNode));
    const composedPath =
      typeof (event as any).composedPath === "function"
        ? (event as any).composedPath()
        : [];
    const isInsideByPath =
      Array.isArray(composedPath) && composedPath.includes(containerRef.current);

    if (!isInsideByTarget && !isInsideByPath) {
      setIsInputFocused(false);
      // Re-enable selected state styling if input has content
      if (address && address.trim().length > 0) {
        setIsSelected(true);
      }
    }
  };

  useEventListener("mousedown", handleClickOutside);
  useEventListener("touchstart", handleClickOutside);

  const toSlugPart = (value: string) => value.toLowerCase().trim().replace(/\s+/g, "-");

  // A resolved street/label can arrive as a FULL multi-part address joined by
  // commas or " - " (curated UAE rows carry e.g.
  // "Qatar - World Islands - Dubai - United Arab Emirates" in streetName/
  // description). The deepest URL segment must be only its leading place name,
  // never the whole address — otherwise the entire label was slugified into one
  // giant district segment (`/ae/dubai/dubai/qatar---world-islands---dubai---united-arab-emirates`).
  const firstLabelSegment = (value: string) =>
    (value || "").split(/,|\s[-–]\s/)[0].trim();

  const buildSearchUrlFromSuggestion = (selected: string, suggestion?: LocationSuggestion | null) => {
    const countryShortRaw = (suggestion?.countryCode || "").toLowerCase();
    const countryShort = countryShortRaw === "gb" ? "uk" : countryShortRaw;
    const country = countryShort || "us";
    const stateShort = suggestion?.stateCode || "";
    const stateLong = suggestion?.state || "";
    const rawCity = suggestion?.city?.trim() || "";
    const resultType = suggestion?.resultType?.toLowerCase() || "";
    const districtLabel = firstLabelSegment(suggestion?.district || "");
    const usesDistrictAsCity =
      !rawCity &&
      Boolean(districtLabel) &&
      ["suburb", "street", "district"].includes(resultType);
    const city = usesDistrictAsCity
      ? districtLabel.replace(/\s+city$/i, "").trim() || districtLabel
      : rawCity;
    const rawStreetName = suggestion?.streetName?.trim() || "";
    // Slug ONLY the leading place name — a curated row whose streetName is the
    // full "Qatar - World Islands - Dubai - United Arab Emirates" label must
    // yield the district `qatar`, not the whole address.
    const streetLabel = firstLabelSegment(rawStreetName);
    const streetNameSlug = streetLabel ? toSlugPart(streetLabel) : "";
    const useStateShort = ["us", "au", "za"].includes(country);

    // Fall back to whichever state value exists — a suggestion carrying only
    // one of state/stateCode must not lose its state segment (a curated
    // "North Sydney" pick with empty stateCode collapsed to /au/sydney).
    const statePart = toSlugPart(
      useStateShort ? stateShort || stateLong : stateLong || stateShort,
    );
    const cityPart = toSlugPart(city);

    // District / suburb / street is the deepest path segment (e.g.
    // `/au/nsw/sydney/sydney-cbd`). The backend has no dedicated field for it,
    // so derive it from the resolved suggestion:
    //  - a street/POI result carries `streetName` (e.g. "Clarence Street")
    //  - a suburb/locality has it as the leading part of `description`
    //    (e.g. "Sydney CBD, Sydney, Australia" -> "Sydney CBD"), distinct from city.
    const descriptionLead = firstLabelSegment(suggestion?.description || "");
    // Google's city/suburb label carries the state IN the first segment with
    // no comma ("Melbourne VIC", "Chippendale NSW"). City picks can also be
    // "Dubai - United Arab Emirates". Strip those trailing state/country
    // qualifiers before treating the lead as a district — otherwise a city
    // search lands on `/ae/dubai/dubai/dubai---united-arab-emirates`.
    const stripTrailingQualifier = (value: string) => {
      let out = value;
      const countryName = getCountryName(country);
      for (const token of [stateShort, stateLong, countryName, country, countryShortRaw]) {
        const t = (token || "").trim();
        if (!t) continue;
        const esc = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        out = out.replace(new RegExp(`(?:\\s*[-,]\\s*|\\s+)${esc}$`, "i"), "").trim();
      }
      return out;
    };
    const leadCore = stripTrailingQualifier(descriptionLead);
    // The deepest segment is Google's own `route` (streetName) when present — a
    // real street. Otherwise the label LEAD, but ONLY for an area-type result
    // (suburb/street/district): a POI/island lead is its own name, never a
    // district (Qatar-island must not become `/qatar`), and a `city` result
    // like "Chippendale" is already the city slot so it takes no deeper
    // segment. A curated DB pick carries no resultType and keeps its behavior.
    // NOTE: Google's structured `district` field is administrative_area_level_2
    // — the LGA CONTAINING the pick ("City of Sydney" for Chippendale), a
    // PARENT, not a child — so it is deliberately NOT used as a deeper segment.
    const areaResultType =
      !suggestion?.resultType ||
      ["suburb", "street", "district"].includes(
        resultType,
      );
    const currentResultIsParentDistrict =
      usesDistrictAsCity && resultType === "district";
    let districtPart = "";
    if (streetNameSlug) {
      districtPart = streetNameSlug;
    } else if (
      !currentResultIsParentDistrict &&
      areaResultType &&
      leadCore &&
      leadCore.toLowerCase() !== city.toLowerCase() &&
      toSlugPart(leadCore) !== statePart &&
      toSlugPart(leadCore) !== cityPart
    ) {
      districtPart = toSlugPart(leadCore);
    }

    const urlParts: string[] = [];
    if (statePart) urlParts.push(statePart);
    // UAE cities often share the emirate name (Dubai/Dubai). Don't emit
    // `/ae/dubai/dubai` — one segment is enough for the city page.
    if (cityPart && cityPart !== statePart) urlParts.push(cityPart);
    // A district only makes sense below a state + city.
    if (
      statePart &&
      cityPart &&
      districtPart &&
      districtPart !== cityPart &&
      districtPart !== statePart
    ) {
      urlParts.push(districtPart);
    } else if (statePart && !cityPart && districtPart && districtPart !== statePart) {
      // An area pick with NO city (e.g. an LGA like "North Sydney Council")
      // otherwise collapses to the bare
      // STATE page (/au/nsw), dropping the pick entirely — the backend then
      // serves the state polygon instead of the picked area's. Carry the
      // area name in the city slot (/au/nsw/north-sydney-council); the
      // backend resolves it by curated name or uses the supplied Google point.
      urlParts.push(districtPart);
    }

    // New property pages live at /{country}/{state}/{city} (country always prefixed).
    // With no state/city we fall back to the country landing page.
    let urlString = "";
    if (urlParts.length > 0) {
      urlString = `/${country}/${urlParts.join("/")}`;
    } else {
      urlString = `/${country}/office-space-${toSlugPart(getCountryName(country))}`;
    }

    // Keep the location coordinates/text out of the URL so the path stays clean
    // (`/au/nsw/sydney`). They're persisted in the background instead, where the
    // loader/map and homepage redirect can still read them. User-applied filters
    // are the only things that belong in the query string.
    const hasCoordinates = Number.isFinite(suggestion?.lat) && Number.isFinite(suggestion?.lng);
    persistSearchLocationContext({
      latitude: hasCoordinates ? Number(suggestion?.lat) : undefined,
      longitude: hasCoordinates ? Number(suggestion?.lng) : undefined,
      zoom: hasCoordinates ? 15 : undefined,
      streetName: rawStreetName || undefined,
      text: resolvePersistedSearchLocationText(selected, suggestion),
      country,
    });

    if (selected) {
      const urlWithoutPlaceId = stripPlaceIdFromSearchUrl(urlString.startsWith("/") ? urlString : `/${urlString}`);
      updateRecentSearches(selected, urlWithoutPlaceId, recentSearches, setRecentSearches, {
        placeId: suggestion?.placeId,
        latitude: hasCoordinates ? Number(suggestion?.lat) : undefined,
        longitude: hasCoordinates ? Number(suggestion?.lng) : undefined,
        country,
      });
    }

    const normalized = urlString.startsWith("/") ? urlString : `/${urlString}`;
    return stripPlaceIdFromSearchUrl(normalized);
  };

  const resolveSuggestionByText = async (
    text: string,
    placeId?: string | null,
  ): Promise<LocationSuggestion | null> => {
    if (!text) return null;
    const countryName = routeParams?.country ? getCountryName(routeParams.country) : "";
    try {
      const params = new URLSearchParams({ text });
      if (placeId) params.set("placeId", placeId);
      // Bias the geocode to the current country so ambiguous names (e.g. "Pitt
      // Street") resolve in-country.
      if (countryName) params.set("country", countryName);
      // Page the user searched FROM — recorded on the places_cache entry so we
      // can trace which pages trigger Google calls (cost/abuse).
      if (location?.pathname) params.set("srcUrl", location.pathname);

      const response = await fetch(`${PLACES_RESOLVE_URL}?${params.toString()}`);
      if (response.ok) {
        const suggestion = (await response.json()) as LocationSuggestion | null;
        if (suggestion?.placeId) return suggestion;
      }
    } catch {
      return null;
    }
    return null;
  };

  useEffect(() => {
    // Require at least MIN_QUERY_LENGTH chars before hitting the API.
    if (!isInputFocused || isSelected || address.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      // The dropdown session is over: any in-flight request must neither
      // paint (floor the painted-id past every issued id) nor keep running.
      lastPaintedIdRef.current = requestIdRef.current;
      pendingRef.current?.controller.abort();
      pendingRef.current = null;
      return;
    }

    // Scope property provider/address suggestions to the current country
    // (from the /{country}/... route). getCountryName maps the code to the
    // name stored in property `country__c` (e.g. "au" -> "Australia").
    const countryName = routeParams?.country ? getCountryName(routeParams.country) : "";
    const trimmedText = address.trim().toLowerCase();
    const cacheKey = `${countryName}::${trimmedText}`;

    // Session cache: a text we've already fetched renders instantly with no
    // request (backspacing, re-typing). The backend has its own 10-min
    // session cache, so skipping the refetch loses nothing meaningful.
    const cachedExact = suggestionCacheRef.current.get(cacheKey);
    if (cachedExact) {
      setSuggestions(withoutOfficeNames(cachedExact.data));
      setLoading(false);
      lastPaintedIdRef.current = requestIdRef.current;
      pendingRef.current?.controller.abort();
      pendingRef.current = null;
      return;
    }

    // First-time text: keep the dropdown useful while the request runs by
    // showing the results of the longest already-fetched PREFIX of what's
    // being typed ("sydne"'s results stay up while "sydney" fetches) —
    // fresh data replaces them when it arrives.
    let bestPrefix: { text: string; data: LocationSuggestion[] } | null = null;
    for (const entry of suggestionCacheRef.current.values()) {
      if (
        entry.country === countryName &&
        trimmedText.startsWith(entry.text) &&
        (!bestPrefix || entry.text.length > bestPrefix.text.length)
      ) {
        bestPrefix = entry;
      }
    }
    if (bestPrefix) {
      setSuggestions(withoutOfficeNames(bestPrefix.data));
    }

    const currentRequestId = ++requestIdRef.current;
    const debounceId = window.setTimeout(() => {
      pendingRef.current?.controller.abort();
      const controller = new AbortController();
      pendingRef.current = { id: currentRequestId, controller };
      setLoading(true);
      (async () => {
        try {
          const params = new URLSearchParams({ text: address, limit: "5" });
          if (countryName) params.set("country", countryName);

          const response = await fetch(
            `${PLACES_AUTOCOMPLETE_URL}?${params.toString()}`,
            { signal: controller.signal },
          );
          const data = await response.json();
          const nextSuggestions: LocationSuggestion[] = (
            Array.isArray(data) ? data : []
          ).filter((item: LocationSuggestion) => item && item.description);

          if (suggestionCacheRef.current.size >= SUGGESTION_CACHE_MAX) {
            const oldestKey = suggestionCacheRef.current.keys().next().value;
            if (oldestKey !== undefined) suggestionCacheRef.current.delete(oldestKey);
          }
          suggestionCacheRef.current.set(cacheKey, {
            text: trimmedText,
            country: countryName,
            data: nextSuggestions,
          });

          if (currentRequestId > lastPaintedIdRef.current) {
            lastPaintedIdRef.current = currentRequestId;
            setSuggestions(withoutOfficeNames(nextSuggestions));
          }
        } catch (error: any) {
          if (error?.name !== "AbortError" && requestIdRef.current === currentRequestId) {
            setSuggestions([]);
          }
        } finally {
          if (pendingRef.current?.id === currentRequestId) {
            pendingRef.current = null;
          }
          if (requestIdRef.current === currentRequestId) {
            setLoading(false);
          }
        }
      })();
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceId);
      if (pendingRef.current?.id === currentRequestId) {
        pendingRef.current.controller.abort();
        pendingRef.current = null;
      }
    };
  }, [address, isInputFocused, isSelected, showOfficeNames]);

  const finalizeLocationSelection = (
    selected: string,
    searchUrl: string,
    placeId?: string | null,
  ) => {
    const normalizedUrl = stripPlaceIdFromSearchUrl(
      searchUrl.startsWith("/") ? searchUrl : `/${searchUrl}`,
    );
    const finalUrl = convertSearchUrlToLocationPage(normalizedUrl);
    if (isValidPersistedPlaceId(placeId)) {
      // Record WHICH path this pick is for (the final, converted one the
      // loader will see), so loaders honor the placeId there at any route
      // level — an area pick lands on a city-level path, where the
      // stale-cookie guards would otherwise drop it.
      persistSearchPlaceId(placeId, finalUrl.split("?")[0]);
    } else {
      clearSearchPlaceId();
    }
    onLocationSelect(finalUrl, selected);
    setSuggestions([]);
    inputRef.current?.blur();
    setIsInputFocused(false);
  };

  const restoreRecentSearchContext = (storedRecent?: {
    title?: string;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
  }) => {
    if (
      storedRecent?.latitude != null &&
      storedRecent?.longitude != null &&
      Number.isFinite(storedRecent.latitude) &&
      Number.isFinite(storedRecent.longitude)
    ) {
      persistSearchLocationContext({
        latitude: storedRecent.latitude,
        longitude: storedRecent.longitude,
        zoom: 15,
        text: storedRecent.title,
        country: storedRecent.country,
      });
    }
  };

  const onSelectRecentSearch = async (title: string, urlString: string) => {
    setIsSelected(true);
    setAddress(title);
    const storedRecent = recentSearches.find((item) => item.title === title);
    const normalizedStored = stripPlaceIdFromSearchUrl(
      urlString.startsWith("/") ? urlString : `/${urlString}`,
    );
    const searchUrl = convertSearchUrlToLocationPage(normalizedStored);

    // Always use the stored URL. Re-resolving by title can return a different
    // POI (e.g. a building) than the suburb/area the user originally picked.
    restoreRecentSearchContext(storedRecent ?? { title });

    if (typeof searchUrl === "string") {
      finalizeLocationSelection(title, searchUrl, storedRecent?.placeId || null);
    }
  };

  /**
   * Nearby / locate-fixed: build the search URL from the IP payload directly.
   * Do NOT re-resolve the label via Places biased to the page country — that
   * fails when the user is abroad (e.g. Karachi on /au) and then hard-falls
   * back to `/us/office-space-united-states`.
   */
  const selectFromIpLocation = (
    selected: string,
    ipLocation: IpLocationForSearchPath,
  ) => {
    const searchUrl = buildSearchUrlFromIpLocation(ipLocation);
    if (!searchUrl) {
      setIsSelected(false);
      return;
    }

    setIsSelected(true);
    setAddress(selected);

    const country = normalizeRegionCode(ipLocation.countryCode) || undefined;
    const hasCoordinates =
      Number.isFinite(ipLocation.lat) && Number.isFinite(ipLocation.lon);

    persistSearchLocationContext({
      latitude: hasCoordinates ? Number(ipLocation.lat) : undefined,
      longitude: hasCoordinates ? Number(ipLocation.lon) : undefined,
      zoom: hasCoordinates ? 15 : undefined,
      text: selected,
      country,
    });

    updateRecentSearches(selected, searchUrl, recentSearches, setRecentSearches, {
      latitude: hasCoordinates ? Number(ipLocation.lat) : undefined,
      longitude: hasCoordinates ? Number(ipLocation.lon) : undefined,
      country,
    });

    finalizeLocationSelection(selected, searchUrl, null);
  };

  /**
   * Record which dropdown suggestion the user picked, and the text they typed to
   * find it. Fire-and-forget: analytics must never delay the search navigation,
   * and TelemetryService already swallows its own errors.
   */
  const trackLocationClick = (
    suggestion: LocationSuggestion,
    searchUrl?: string,
  ) => {
    // `address` still holds the typed query here — setAddress(selected) only
    // takes effect on the next render.
    const queryText = address.trim();
    if (!queryText) return;

    const hasCoordinates =
      Number.isFinite(suggestion.lat) && Number.isFinite(suggestion.lng);
    void TelemetryService.postSearchLocationClick({
      query_text: queryText,
      place_id: suggestion.placeId,
      source: suggestion.source,
      description: suggestion.description,
      latitude: hasCoordinates ? Number(suggestion.lat) : undefined,
      longitude: hasCoordinates ? Number(suggestion.lng) : undefined,
      city: suggestion.city,
      state: suggestion.state,
      state_code: suggestion.stateCode,
      country_code: suggestion.countryCode,
      street_name: suggestion.streetName,
      search_url: searchUrl,
    });
  };

  const onSelectLocation = async (selected: string, placeId: string | null) => {
    if (!selected) {
      clearSearchPlaceId();
      setIsSelected(false);
      onLocationSelect(null);
      return;
    }

    setIsSelected(true);
    setAddress(selected);

    // The suggestion the user actually clicked. Kept separate from
    // `liveSuggestion` below, whose `suggestions[0]` fallback would attribute a
    // recent-search click to whichever suggestion happened to be listed first.
    const clickedSuggestion =
      suggestions.find((item) => item.placeId === placeId) || null;

    // A live dropdown suggestion always wins: rebuild the URL from it so we get
    // the current path shape (state/city/district). This must take precedence
    // over a stored recent search with the same title, whose URL may be stale
    // (e.g. missing the district segment or pointing at a legacy route).
    const liveSuggestion = clickedSuggestion || suggestions[0] || null;
    if (liveSuggestion) {
      const resolvedSuggestion =
        liveSuggestion.source === "db"
          ? liveSuggestion
          : await resolveSuggestionByText(selected, liveSuggestion.placeId);
      if (!resolvedSuggestion) {
        setIsSelected(false);
        return;
      }
      const searchUrl = buildSearchUrlFromSuggestion(selected, resolvedSuggestion);
      if (clickedSuggestion) {
        trackLocationClick(
          resolvedSuggestion,
          typeof searchUrl === "string" ? searchUrl : undefined,
        );
      }
      if (typeof searchUrl === "string") {
        // A property (DB) suggestion's placeId is "property:<sfid>" — not
        // geocodable, but location-polygon now understands it: it fetches
        // that exact property by sfid (using its location_places__c link and
        // pinning it) instead of reverse-matching the address text. So pass
        // it through like any other placeId.
        //
        // A POI/establishment pick (mall, landmark) keeps its placeId too, so
        // the map centres ON that point. Its box would resolve to the POI's own
        // tiny footprint (a Burj Khalifa placeId once returned a SINGLE
        // property), but the backend's results-escalation now grows the search
        // box around that centre until it surfaces the nearby offices — a Dubai
        // Mall pick opens ON Dubai Mall with ~27 nearby offices, not 1.
        finalizeLocationSelection(
          selected,
          searchUrl,
          resolvedSuggestion.placeId || placeId,
        );
      }
      return;
    }

    // No live suggestion (e.g. geolocate, or a recent search reused without the
    // dropdown loaded): fall back to the stored recent URL, else resolve + build.
    const storedRecent = recentSearches.find((item) => item.title === selected);
    if (storedRecent?.urlString) {
      restoreRecentSearchContext(storedRecent);
      finalizeLocationSelection(
        selected,
        storedRecent.urlString,
        storedRecent.placeId || placeId,
      );
      return;
    }

    const resolvedSuggestion = await resolveSuggestionByText(selected);
    const searchUrl = buildSearchUrlFromSuggestion(selected, resolvedSuggestion);
    if (typeof searchUrl === "string") {
      finalizeLocationSelection(selected, searchUrl, resolvedSuggestion?.placeId || placeId);
    }
  };

  const handleSelectNearby = () => {
    fetchLocation((label, coords, ipLocation) => {
      if (ipLocation) {
        selectFromIpLocation(label, ipLocation);
        return;
      }
      onSelectLocation(label, coords ? JSON.stringify(coords) : null);
    });
  };

  // Free-text keyword search ("Search for '<text>'"). If the user is already on
  // a location page (e.g. /au/nsw/sydney/sydney-cbd), keep it and just add ?q —
  // so the search runs INSIDE the current polygon, preserving its placeId. Only
  // from the home/country root (or an entity page) do we land on the country
  // search page, where the server scopes the map to the matched results.
  const handleFreeTextSearch = (rawText: string) => {
    const text = (rawText || "").trim();
    if (text.length < 2) return;

    setIsSelected(true);

    const country = (routeParams?.country || "us").toLowerCase();
    const segments = location.pathname.split("/").filter(Boolean);
    const ENTITY_PREFIXES = [
      "properties",
      "property",
      "workspaces",
      "buildings",
      "areas",
      "listings",
      "blog",
      "news",
    ];
    const onLocationPage =
      segments.length > 1 &&
      segments[0] === country &&
      !ENTITY_PREFIXES.includes(segments[1]);

    if (onLocationPage) {
      // Preserve the current path (and its persisted placeId/polygon); just set
      // q on top of any existing filters. Don't clear the placeId here.
      const params = new URLSearchParams(location.search);
      params.set("q", text);
      const url = `${location.pathname}?${params.toString()}`;
      setAddress(text);
      setSuggestions([]);
      inputRef.current?.blur();
      setIsInputFocused(false);
      onLocationSelect(convertSearchUrlToLocationPage(url), text);
      return;
    }

    const landing = `/${country}/office-space-${toSlugPart(getCountryName(country))}`;
    finalizeLocationSelection(text, `${landing}?q=${encodeURIComponent(text)}`, null);
  };

  return (
    <div className='w-full'>
      <div className='relative' ref={containerRef}>
        <input
          ref={inputRef}
          value={address}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder || `${loading ? 'Loading....' : 'Search a city, area or postcode'}`}
          className={cn(
            // Default base styles
            "flex min-h-11 w-full rounded-full border border-input bg-background pl-12 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pr-10",
            mobile ? "text-base" : "text-base",
            // Conditional internal styles
            mobile && "bg-transparent",
            isSelected && inputStyleClassName,
            // External classes passed from parent components
            className
          )}
          onFocus={() => {
            setIsInputFocused(true);
          }}
        />
        <MapPin className={`absolute left-4 top-1/2 transform -translate-y-1/2  ${mobile ? 'h-4 w-4' : 'h-5 w-5 text-gray-500'} `} />
        {isSelected &&
          <CircleX
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              isManualClearRef.current = true;
              setAddress("");
              setIsSelected(false);
              setSuggestions([]);
              onSelectLocation("", null);

              // Focus back on the input to trigger dropdown suggestions
              inputRef.current?.focus();
              setIsInputFocused(true);

              setTimeout(() => {
                isManualClearRef.current = false;
              }, 100);
            }}
            className={`absolute right-4 cursor-pointer top-1/2 transform -translate-y-1/2 z-50 ${mobile ? 'h-4 w-4' : 'h-5 w-5 text-gray-500'
              }`}
          />
        }
        <div className={`${fixed ? 'fixed' : ''} bg-white rounded-lg z-50`} style={{ width: mobile ? inputWidth + 80 : inputWidth }}>
          {((!isSelected && isInputFocused) || suggestions.length) ? (
            <div
              className={`absolute left-0 right-0 min-h-[100px] max-h-[400px] min-w-[330px] md:w-[98.5%] overflow-y-auto [scrollbar-width:none] px-3 pb-3 pt-2 m-auto shadow-2xl bg-white rounded-b-lg z-[999999] origin-top animate-in fade-in slide-in-from-top-1 duration-200 ease-out`}
            >
              <RecentSearches
                address={address}
                loading={loading}
                suggestions={suggestions}
                showOfficeNames={showOfficeNames}
                getSuggestionItemProps={(suggestion: LocationSuggestion) => ({
                  onMouseDown: (event: Event) => {
                    event.preventDefault();
                    onSelectLocation(suggestion.description, suggestion.placeId);
                  },
                })}
                recentSearches={recentSearches}
                handleSelectLocation={(location, coords) =>
                  onSelectLocation(
                    location,
                    coords ? JSON.stringify(coords) : null,
                  )
                }
                onSelectNearby={handleSelectNearby}
                handleSelectWithPlaceId={(location, placeId) => {
                  onSelectLocation(location, placeId);
                }}
                handleSelectRecentSearch={onSelectRecentSearch}
                // In a modal we surface ONLY the API results (locations +
                // properties) — no free-text "Search for …" row.
                onFreeTextSearch={inModal ? undefined : handleFreeTextSearch}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AutoPlaces;
